import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import { PAGINATION_DOTS, usePageNumbers } from './usePageNumbers'
import { MAX_PAGES_TO_SHOW } from './usePageNumbers'
import { ArrowLeftAgent, ArrowRightAgent, ArrowRightCustomer, ArrowLeftCustomer } from '../../components/Svg'

const MIN_INDEX_FOR_DOTS = 1
const MAX_INDEX_FOR_DOTS = MAX_PAGES_TO_SHOW - 1

const Paginate = ({ currentPage, totalPages, onPageChange, className, type = 'agent' }) => {
  const current = parseInt(currentPage, 10);
  const total = parseInt(totalPages, 10);

  const [pageNumbers] = usePageNumbers(total, current)

  const LeftIcon = type === 'agent' ? ArrowLeftAgent : ArrowLeftCustomer
  const RightIcon = type === 'agent' ? ArrowRightAgent : ArrowRightCustomer

  const handleClick = useCallback(
    pageNumber => () => {
      onPageChange(pageNumber)
    },
    [total]
  )

  const handleTheeDotsClick = useCallback(
    clickedIndex => () => {
      // clickedIndex is always 1 or 5
      const prevNumber = pageNumbers[clickedIndex + 1] - 1
      const nextNumber = pageNumbers[clickedIndex - 1] + 1

      const mapByClickedIndex = {
        [MIN_INDEX_FOR_DOTS]: prevNumber,
        [MAX_INDEX_FOR_DOTS]: nextNumber
      }

      const pageNumber = mapByClickedIndex[clickedIndex]
      return onPageChange(pageNumber)
    },
    [pageNumbers]
  )

  if (total <= 1) {
    return null
  }

  return (
    <nav className={cn('PaginateCustom', className)}>
      <div className={cn('PaginateCustom-control', { isDisabled: current === 1 })}>
        <LeftIcon className='PaginateCustom-iconPrev' onClick={handleClick(current - 1)} />
      </div>

      {pageNumbers.map((pageNumber, idx) => {
        return pageNumber === PAGINATION_DOTS ? (
          <div
            key={`${idx}${pageNumber}`}
            className='PaginateCustom-content'
            onClick={handleTheeDotsClick(idx)}
            role='button'
            tabIndex={0}
            onKeyDown={handleTheeDotsClick(idx)}
          >
            {pageNumber}
          </div>
        ) : (
          <div
            key={`${current}-${pageNumber}`}
            className={cn(
              'PaginateCustom-content',
              { agentStyle: type === 'agent' },
              { customerStyle: type === 'customer' },
              { 'PaginateCustom-content--active': pageNumber === current }
            )}
            onClick={handleClick(Number(pageNumber))}
            role='button'
            tabIndex={0}
            onKeyDown={handleClick(Number(pageNumber))}
          >
            {pageNumber}
          </div>
        )
      })}

      <div className={cn('PaginateCustom-control', { isDisabled: current === total })}>
        <RightIcon className='PaginateCustom-iconNext' onClick={handleClick(current + 1)} />
      </div>
    </nav>
  )
}

Paginate.propTypes = {
  currentPage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  totalPages: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onPageChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  type: PropTypes.oneOf(['agent', 'customer'])
}

export default Paginate
